import React, { useEffect, useMemo, useRef, useState } from 'react';
import { convertToRaw, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import { ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { ReactComponent as DownArrow } from '@/assets/icons/down-arrow-gray-dark-01.svg';
import { useTechpackCommentsMutation } from '@/features/design/api/designDetailsApi';
import { toastError, toastSuccess } from '@/components/Toast';

import EditCommentsTitleModal from './EditComentsTitelModal';

const DEFAULT_COMMENT_SECTIONS = [
	{ id: 'amComment', label: 'AM/MR comments', key: 'amComment' },
	{
		id: 'additionalComment2',
		label: 'Additional comments 2',
		key: 'additionalComment2',
		editable: true,
		headerKey: 'additionalComment2Header'
	},
	{ id: 'gtComment', label: 'GT comments', key: 'gtComment' },

	{
		id: 'additionalComment1',
		label: 'Additional comments 1',
		key: 'additionalComment1',
		editable: true,
		headerKey: 'additionalComment1Header'
	},
	{ id: 'otherComment', label: 'Fabric tech', key: 'otherComment' },

	{
		id: 'additionalComment3',
		label: 'Additional comments 3',
		key: 'additionalComment3',
		editable: true,
		headerKey: 'additionalComment3Header'
	}
];

const toolbarConfig = {
	options: ['inline', 'list', 'history'],
	inline: {
		options: ['bold', 'italic', 'underline']
	},
	list: {
		options: ['unordered', 'ordered']
	}
};

const DEFAULT_TITLES = {
	amComment: 'AM/MR comments',
	gtComment: 'GT comments',
	otherComment: 'Fabric tech',
	additionalComment1: 'Additional comments 1',
	additionalComment2: 'Additional comments 2',
	additionalComment3: 'Additional comments 3'
};

const getInitialCommentsData = (initialData = {}, designDetails = {}) => {
	const sourceData =
		initialData && Object.keys(initialData).length
			? initialData
			: designDetails?.tpPageBottomCommentUpdateRequest || {};

	return {
		amComment: sourceData.amComment || '',
		gtComment: sourceData.gtComment || '',
		otherComment: sourceData.otherComment || '',
		additionalComment1: sourceData.additionalComment1 || '',
		additionalComment2: sourceData.additionalComment2 || '',
		additionalComment3: sourceData.additionalComment3 || '',
		additionalComment1Header: sourceData.additionalComment1Header || DEFAULT_TITLES.additionalComment1,
		additionalComment2Header: sourceData.additionalComment2Header || DEFAULT_TITLES.additionalComment2,
		additionalComment3Header: sourceData.additionalComment3Header || DEFAULT_TITLES.additionalComment3
	};
};

const areCommentValuesEqual = (prev = {}, next = {}) =>
	prev.amComment === next.amComment &&
	prev.gtComment === next.gtComment &&
	prev.otherComment === next.otherComment &&
	prev.additionalComment1 === next.additionalComment1 &&
	prev.additionalComment2 === next.additionalComment2 &&
	prev.additionalComment3 === next.additionalComment3;

const areTitleValuesEqual = (prev = {}, next = {}) =>
	prev.amComment === next.amComment &&
	prev.gtComment === next.gtComment &&
	prev.otherComment === next.otherComment &&
	prev.additionalComment1 === next.additionalComment1 &&
	prev.additionalComment2 === next.additionalComment2 &&
	prev.additionalComment3 === next.additionalComment3;

const getCommentsState = (data = {}) => ({
	amComment: data.amComment,
	gtComment: data.gtComment,
	otherComment: data.otherComment,
	additionalComment1: data.additionalComment1,
	additionalComment2: data.additionalComment2,
	additionalComment3: data.additionalComment3
});

const getTitlesState = (data = {}) => ({
	amComment: DEFAULT_TITLES.amComment,
	gtComment: DEFAULT_TITLES.gtComment,
	otherComment: DEFAULT_TITLES.otherComment,
	additionalComment1: data.additionalComment1Header,
	additionalComment2: data.additionalComment2Header,
	additionalComment3: data.additionalComment3Header
});

const htmlToEditorState = (html) => {
	if (!html) return EditorState.createEmpty();
	const trimmed = html.trim();
	if (!trimmed || trimmed === '<p></p>') return EditorState.createEmpty();
	const { contentBlocks, entityMap } = htmlToDraft(trimmed);
	const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
	return EditorState.createWithContent(contentState);
};

const CommentEditor = ({ placeholder, onChange, value = '' }) => {
	const [editorState, setEditorState] = useState(() => htmlToEditorState(value));
	const [isFocused, setIsFocused] = useState(false);
	const latestExternalValueRef = useRef(value || '');

	useEffect(() => {
		const safeValue = value || '';
		if (safeValue === latestExternalValueRef.current) return;

		latestExternalValueRef.current = safeValue;
		if (isFocused) return;

		setEditorState(htmlToEditorState(safeValue));
	}, [value, isFocused]);

	const onEditorStateChange = (state) => {
		setEditorState(state);
		const html = draftToHtml(convertToRaw(state.getCurrentContent()));
		latestExternalValueRef.current = html;
		onChange(html);
	};

	return (
		<div className={`techpack-comment-editor border p-2 ${isFocused ? 'focused' : ''}`}>
			<Editor
				editorState={editorState}
				onEditorStateChange={onEditorStateChange}
				onFocus={() => {
					setIsFocused(true);
				}}
				onBlur={() => {
					setIsFocused(false);
				}}
				toolbar={toolbarConfig}
				placeholder={placeholder}
				editorClassName="techpack-editor-content"
				toolbarClassName="techpack-editor-toolbar"
				toolbarHidden={!isFocused}
			/>
		</div>
	);
};

const TechpackReviewComments = ({ initialData = {}, productId, designDetails }) => {
	const [editingTitle, setEditingTitle] = useState(null);
	const [techpackComments, { isLoading: isTechpackCommentsLoading }] = useTechpackCommentsMutation();
	const initialCommentsData = useMemo(
		() => getInitialCommentsData(initialData, designDetails),
		[initialData, designDetails]
	);
	const lastHydratedRef = useRef({
		comments: getCommentsState(initialCommentsData),
		titles: getTitlesState(initialCommentsData)
	});

	// State for comment content
	const [comments, setComments] = useState(() => getCommentsState(initialCommentsData));

	// State for editable headers
	const [titles, setTitles] = useState(() => getTitlesState(initialCommentsData));

	useEffect(() => {
		const nextComments = getCommentsState(initialCommentsData);
		const nextTitles = getTitlesState(initialCommentsData);
		const sourceCommentsChanged = !areCommentValuesEqual(lastHydratedRef.current.comments, nextComments);
		const sourceTitlesChanged = !areTitleValuesEqual(lastHydratedRef.current.titles, nextTitles);

		if (!sourceCommentsChanged && !sourceTitlesChanged) return;

		lastHydratedRef.current = { comments: nextComments, titles: nextTitles };

		setComments((prev) => (areCommentValuesEqual(prev, nextComments) ? prev : nextComments));
		setTitles((prev) => (areTitleValuesEqual(prev, nextTitles) ? prev : nextTitles));
	}, [initialCommentsData]);

	const handleCommentChange = (commentKey, value) => {
		setComments((prev) => ({ ...prev, [commentKey]: value }));
	};

	const handleTitleEdit = (commentKey, value) => {
		setTitles((prev) => ({ ...prev, [commentKey]: value }));
	};

	const buildRequestBody = () => {
		return {
			amComment: comments.amComment,
			gtComment: comments.gtComment,
			otherComment: comments.otherComment,
			additionalComment1: comments.additionalComment1,
			additionalComment2: comments.additionalComment2,
			additionalComment3: comments.additionalComment3,
			additionalComment1Header: titles.additionalComment1,
			additionalComment2Header: titles.additionalComment2,
			additionalComment3Header: titles.additionalComment3
		};
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const requestBody = { ...buildRequestBody() };
		try {
			await techpackComments({ productId, body: requestBody }).unwrap();
			toastSuccess('Techpack comments saved successfully!');
		} catch (error) {
			toastError(error?.data?.message || 'Error submitting techpack comments');
		}
	};

	return (
		<>
			<form onSubmit={handleSubmit}>
				<div className="tehckpack-comments-form techpack-checklist">
					<div className="common-accordion bg-white">
						<div className="accordion border-0" id="accordionExample">
							<div className="card mb-0">
								<div className="card-header" id="headingOne">
									<h2 className="mb-0">
										<button
											type="button"
											className="btn btn-link btn-block pb-0 bg-white d-flex justify-content text-left gray_dark_01 regular-14 weight-600 p-0"
											data-toggle="collapse"
											data-target="#tableCollapseOne"
											aria-expanded="true"
											aria-controls="tableCollapseOne"
										>
											<div className="d-flex align-items-center justify-content-between w-100">
												<p className="medium-14 gray_dark_01">
													Techpack review comments:{' '}
													<span>{DEFAULT_COMMENT_SECTIONS.length}</span>
												</p>
												<div className="arrow-down todo-arrow-down">
													<DownArrow />
												</div>
											</div>
										</button>
									</h2>
								</div>
								<div
									id="tableCollapseOne"
									className="collapse show"
									aria-labelledby="headingOne"
									data-parent="#accordionExample"
								>
									<div className="card-body pt-3 px-0 border-0 bg-white">
										<div className="comments-form">
											<div className="row">
												{DEFAULT_COMMENT_SECTIONS.map((section, index) => (
													<div
														key={section.id}
														className={`col-md-6 mb-3 single-comment-section ${
															index % 2 === 0 ? 'even' : 'odd'
														}`}
													>
														<div className="edit-title-header d-flex align-items-center gap-2 mb-1">
															<p className="regular-14 gray_dark_01 mb-0">
																{titles[section.id]}
															</p>
															{section.editable && (
																<button
																	type="button"
																	className="btn btn-link title-btn p-0 regular-12"
																	onClick={() =>
																		setEditingTitle(section.id)
																	}
																>
																	Edit title
																</button>
															)}
														</div>
														<CommentEditor
															placeholder=""
															value={comments[section.key] || ''}
															onChange={(value) =>
																handleCommentChange(section.key, value)
															}
														/>
													</div>
												))}
											</div>
										</div>
										<div className="mt-1 d-flex gap-2 justify-content-end">
											<button
												type="submit"
												className="button size32 outline"
												disabled={isTechpackCommentsLoading}
											>
												{isTechpackCommentsLoading ? 'Saving...' : 'Save Comments'}
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				{editingTitle && (
					<EditCommentsTitleModal
						onClose={() => setEditingTitle(null)}
						isLoading={false}
						defaultValue={titles[editingTitle]}
						onSave={(value) => {
							handleTitleEdit(editingTitle, value);
							setEditingTitle(null);
						}}
					/>
				)}
			</form>
		</>
	);
};

export default TechpackReviewComments;
